import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { makeStyles, Fab, Zoom } from '@material-ui/core'
import { AttachmentProvider } from '../contexts/AttachmentContext'
import { AttachmentViewer } from '../Attachments/AttachmentViewer'
import Icon from '../../../../components/atoms/Icon'
import { useAIThread, useInvokeDenaliAssistant, useRetryMessage } from '../../../../api/denaliAssistant'
import MessageInput from '../common/MessageInput'
import Background from '../../../../providers/BackgroundProvider/Background'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import DenaliLoader from '../common/DenaliLoader'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import MessageList from './MessageList'

const useStyles = makeStyles((theme) => ({
  chatContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row', // Changed to row for side-by-side layout
    overflow: 'hidden' // Prevent the container itself from scrolling
  },

  chatWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0 // Allow flex item to shrink below its content size
  },

  chatContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    // padding: 'var(--padding-lg) var(--padding-md) var(--padding-md)',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    minHeight: 0, // Allows flex child to shrink below content size
    overflow: 'hidden',
    position: 'relative' // For positioning the scroll button
  },
  messagesWrapper: {
    flex: 1,
    minHeight: 0, // Allows flex child to shrink below content size
    overflow: 'hidden',
    overflowY: 'auto', // Auto vertical scrollbar
    scrollBehavior: 'smooth',
    position: 'relative'
  },
  inputSection: {
    flexShrink: 0, // Prevent input from shrinking
    padding: 'var(--padding-md)'
  },
  scrollToBottomButton: {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    backgroundColor: 'var(--surface-bg-color)',
    color: 'var(--color-primary-500)',
    '&:hover': {
      backgroundColor: 'var(--surface-bg-color-d1)'
    }
  }
}))

function ChatInterface () {
  const classes = useStyles()
  const { threadId } = useParams()
  const history = useHistory()
  const messagesEndRef = useRef(null)
  const messagesWrapperRef = useRef(null)
  const messageInputRef = useRef(null)
  const [isAtBottom, setIsAtBottom] = useState(true)

  const isNewThread = threadId === 'new'

  // Fetch thread data
  const { data: threadData, isLoading } = useAIThread(threadId, {
    enabled: !isNewThread
  })

  // Mutations
  const invokeMutation = useInvokeDenaliAssistant()
  const retryMutation = useRetryMessage()

  // Get messages from thread data
  const messages = threadData?.thread?.messages || []

  // Get focus from thread metadata context
  const threadFocus = threadData?.thread?.metadata?.context?.focus || null

  // Check if user is at bottom of scroll
  const checkIfAtBottom = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) return

    // Check if scrolled to bottom (with 50px threshold for floating point errors)
    const isBottom = wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight < 50
    setIsAtBottom(isBottom)
  }, [])

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (wrapper) {
      // Use a large number to ensure we reach the absolute bottom
      wrapper.scrollTop = wrapper.scrollHeight + 1000
      // Double-check after a brief delay
      setTimeout(() => {
        wrapper.scrollTop = wrapper.scrollHeight + 1000
      }, 50)
    }
    setIsAtBottom(true)
  }, [])

  // Auto-scroll to bottom when new messages arrive, but only if user is already at bottom
  useLayoutEffect(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper || !isAtBottom) return

    // Scroll to bottom after DOM updates
    wrapper.scrollTop = wrapper.scrollHeight
  }, [threadData?.thread?.messages, isAtBottom, messages.length])

  // Monitor scroll position
  useEffect(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) return

    const handleScroll = () => {
      checkIfAtBottom()
    }

    // Check initial position after a brief delay
    setTimeout(() => {
      checkIfAtBottom()
      // Scroll to bottom initially
      wrapper.scrollTop = wrapper.scrollHeight + 1000
    }, 100)

    wrapper.addEventListener('scroll', handleScroll)
    return () => wrapper.removeEventListener('scroll', handleScroll)
  }, [checkIfAtBottom])

  const handleSendMessage = useCallback(async (message, attachments = []) => {
    if (!message.trim()) return

    try {
      // Send the message - thread will be created automatically if needed
      const result = await invokeMutation.mutateAsync({
        threadId: isNewThread ? undefined : threadId,
        prompt: message,
        context: {
          currentView: 'ai-assistant-chat'
        },
        options: {
          modelStrategy: 'balanced',
          streamResponse: true,
          includeTrailOfThought: true
        },
        attachments
      })

      // If this was a new thread, update URL to the new thread
      if (isNewThread && result.threadId) {
        history.replace(`/admin/denali/analysis/assistant/chat/${result.threadId}`)
      }

      // Ensure we're at bottom for new messages
      setIsAtBottom(true)
      // Force scroll to bottom after sending
      setTimeout(() => {
        scrollToBottom()
      }, 100)
    } catch (error) {
      console.error('Failed to send message:', error)
      // TODO: Show error toast
    }
  }, [threadId, isNewThread, invokeMutation, history, scrollToBottom])

  const handleRetry = useCallback(async (message) => {
    if (!threadId || !message.threadMessageId) return

    try {
      await retryMutation.mutateAsync({
        threadId,
        threadMessageId: message.threadMessageId
      })
      // The mutation will invalidate the query cache, which will refetch and show updated messages
      // Scroll to bottom to show the new generation
      setIsAtBottom(true)
      setTimeout(() => {
        scrollToBottom()
      }, 100)
    } catch (error) {
      console.error('Failed to retry message:', error)
      // TODO: Show error toast
    }
  }, [threadId, retryMutation, scrollToBottom])

  const isProcessing = invokeMutation.isLoading ||
    messages.some(m => m.isGenerating)

  // Force scroll to bottom during active generation if we were at bottom
  useEffect(() => {
    if (isProcessing && isAtBottom) {
      const wrapper = messagesWrapperRef.current
      if (!wrapper) return

      // More aggressive scrolling during generation
      const scrollInterval = setInterval(() => {
        wrapper.scrollTop = wrapper.scrollHeight + 1000 // Add extra to ensure we reach bottom
      }, 50) // Faster interval

      return () => clearInterval(scrollInterval)
    }
  }, [isProcessing, isAtBottom])

  const parentInfo = useMemo(() => {
    return {
      parentType: 'thread',
      parentId: threadId
    }
  }, [threadId])

  if (!isNewThread && isLoading) {
    return (
      <div className={classes.chatContainer}>
        <FlexColumn className='gap-md items-center justify-center w-full' style={{ padding: '40px' }}>
          <DenaliLoader />
          <FadeIn style={{ color: 'var(--dai-color-text-dim)' }} delay={1} duration={2}>Loading Conversation</FadeIn>
        </FlexColumn>
      </div>
    )
  }

  return (
    <Background
      light='var(--dai-bg)'
      dark='var(--dai-bg)'
    >
      <AttachmentProvider threadId={threadId} focus={threadFocus}>
        <div className={classes.chatContainer}>
          <div className={classes.chatWrapper}>
            <div className={classes.chatContent}>
              <div ref={messagesWrapperRef} className={classes.messagesWrapper}>
                <MessageList
                  parentInfo={parentInfo}
                  messages={messages}
                  isNewThread={isNewThread}
                  onRetry={handleRetry}
                  onSuggestionClick={(prompt) => {
                    // Set the suggestion in the input so user can review/modify before sending
                    messageInputRef.current?.setValue(prompt)
                  }}
                />
                <div ref={messagesEndRef} style={{ height: '1px' }} />
              </div>
              {/* Scroll to bottom button */}
              <Zoom in={!isAtBottom}>
                <Fab
                  className={classes.scrollToBottomButton}
                  size='small'
                  onClick={scrollToBottom}
                  style={{ zIndex: '1000' }}
                  aria-label='Scroll to bottom'
                >
                  <Icon name='chevronDown' />
                </Fab>
              </Zoom>
            </div>
            <div className={classes.inputSection}>
              <MessageInput
                ref={messageInputRef}
                onSend={handleSendMessage}
                disabled={isProcessing}
                placeholder={isNewThread ? 'Start a new conversation...' : 'Type a message...'}
              />
            </div>
          </div>
          <AttachmentViewer />
        </div>
      </AttachmentProvider>
    </Background>
  )
}

export default ChatInterface
